import { Component, HostListener, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { of, Subscription } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { DownloadType } from 'src/app/model/DownloadStatus';
import { SolutionInfo } from 'src/app/model/SolutionInfo';
import { SystemUpdateState, SystemUpdateStates } from 'src/app/model/SystemUpdateState';
import { UploadFileType } from 'src/app/model/Upload';
import { UserInfo } from 'src/app/model/User';
import { AuthenticationService } from 'src/app/service/authentication.service';
import { EventService } from 'src/app/service/event.service';
import { GeneralService } from 'src/app/service/general.service';
import { SoftwareInventoryService } from 'src/app/service/software-inventory.service';
import { Events, MsgPayLoad, WebsocketService } from 'src/app/service/websocket.service';
import { Utils } from 'src/app/utils';
import { ColumnSort, ColumnType, DataTableColumn, DataTableRow } from '../controls/datatable/datatable.component';
import { ModalMessageComponent, ModalMessageType } from '../controls/modal-message/modal-message.component';
import { ModalConfig } from '../controls/modal/modal-config';
import { Modal } from '../controls/modal/modal.service';
import { WizardStep } from '../controls/wizard/wizard.component';
import { DragDropComponent } from '../dialogs/drag-drop/drag-drop.component';
import { DownloadItem } from '../products-overview/products-overview.component';
import { PackageRow } from '../systems-enable/systems-enable.component';
import { DataService } from 'src/app/service/data.service';
import { ProductDataType } from '../products/products.component';

enum SystemUpdateTaskState {
  DOWNLOAD = "download",
  INSTALL = "install",
  COMPLETE = "complete"
}

@Component({
    selector: 'app-system-update',
    templateUrl: './system-update.component.html',
    styleUrls: ['./system-update.component.css'],
    standalone: false
})
export class SystemUpdateComponent implements OnInit {

  steps: WizardStep[] = [
    { 'id': SystemUpdateTaskState.DOWNLOAD, 'name': $localize`Download` },
    { 'id': SystemUpdateTaskState.INSTALL, 'name': $localize`Install` },
    { 'id': SystemUpdateTaskState.COMPLETE, 'name': $localize`Complete` }
  ]

  index: number = 0;
  state: SystemUpdateTaskState = SystemUpdateTaskState.DOWNLOAD;
  SystemUpdateTaskState = SystemUpdateTaskState;

  packageCols: DataTableColumn[] = Array<DataTableColumn>(
    new DataTableColumn('displayType', $localize`Type`, ColumnType.STRING, true, "25", ColumnSort.ASC),
    new DataTableColumn('name', $localize`Name`, ColumnType.STRING, false, "40"),
    new DataTableColumn('status', $localize`Status`, ColumnType.STATUSEXT, false, "25"),
    new DataTableColumn("url", $localize`Download`, ColumnType.DOWNLOADLINK, false, "10")
  );

  solutionInfo: SolutionInfo;

  isManadatory: boolean = false;
  isUpToDate: boolean = false;
  isOnline: boolean;
  isProxyRequired: boolean;
  itemsToDownload: DownloadItem = new DownloadItem();

  isValidatingPackage: boolean = false;
  isError: boolean = false;
  errorMessage: string;
  needsDownload: boolean = false;
  packageRows: DataTableRow[] = [];
  validationData: any;
  isValidatingPackageComplete: boolean = false;
  isPackageError: boolean = false;
  isPackageDownloadActive: boolean = false;
  isDownloading: boolean = false;
  isInstalling: boolean = false;
  isComplete: boolean = false;
  installationProgress: string;

  installTaskTimer: any;
  failedTime: number = 1200000;

  systemUpdateState: SystemUpdateState;

  solutionInfoSubscription: Subscription;
  eventMessageSubscription: Subscription;
  dataSubscription: Subscription;

  constructor(protected router: Router,
    protected modal: Modal,
    protected eventService: EventService,
    protected authService: AuthenticationService,
    protected softwareService: SoftwareInventoryService,
    protected generalService: GeneralService,
    protected wsService: WebsocketService,
    protected cookieSrv: CookieService,
    protected dataService: DataService) { }

  @HostListener('window:beforeunload', ['$event'])
  unloadEventHandler(event: any) {
    event.preventDefault();
    //browsers won't show this text but just in case
    event.returnValue = 'Are you sure?';
  }

  ngOnInit(): void {
    this.solutionInfoSubscription = this.eventService.currentSolutionInfo$.subscribe(newSolutionInfo => {
      this.solutionInfo = newSolutionInfo;
      this.isManadatory = this.solutionInfo.manifestStatus.forceUpdate;
      this.isOnline = this.authService.getLoggedInUser().settings.isOnlineMode;
      this.goToDownload();
    });

    this.eventMessageSubscription = this.eventService.eventMessage$.subscribe(msg => {
      if (msg.eventType == Events.DownloadCompleted) {
        this.processDownloadComplete(msg);
      } else if (msg.eventType == Events.ManagerConnected) {
        if (this.isInstalling) {
          clearTimeout(this.installTaskTimer);
          this.installTaskTimer = undefined;
          this.isComplete = true;
          this.isInstalling = false;
          this.goToComplete();
        }
      }
    });

    this.dataSubscription = this.dataService.getData().subscribe((data:any) => {
      if (data.type == ProductDataType.DownloadStarted) {
        this.setDownloadInProgress();
      } 
    });
  }

  ngOnDestroy(): void {
    //set the System Update state to complete when leaving the page
    this.systemUpdateState.state = SystemUpdateStates.Complete;
    this.eventService.broadcastSystemUpdateState(this.systemUpdateState);
    this.solutionInfoSubscription.unsubscribe();
    this.eventMessageSubscription.unsubscribe();
    this.dataSubscription.unsubscribe();
  }

  canDeactivate(): boolean {
    return (this.isComplete || this.isError || this.isUpToDate);
  }

  getDeactivateModalMessageConfig(): ModalConfig {
    return Utils.interuptUpgrade(this.systemUpdateState);
  }

  cancel(): void {
    if (this.isComplete) {
      this.router.navigate(['home'])
        .then(() => {
          window.location.reload();
        });
    } else {
      this.router.navigate(['home']);
    }
  }

  next(): void {
    this.isError = false;
    if (this.state == SystemUpdateTaskState.DOWNLOAD) {
      this.goToInstall();
    } else if (this.state == SystemUpdateTaskState.INSTALL) {
      this.goToComplete();
    }
  }

  back(): void {
    this.isError = false;
    if (this.state == SystemUpdateTaskState.INSTALL) {
      this.goToDownload();
    } else if (this.state == SystemUpdateTaskState.COMPLETE) {
      this.goToInstall();
    }
  }

  goToDownload(): void {
    this.index = 0;
    this.state = SystemUpdateTaskState.DOWNLOAD;
    this.systemUpdateState = new SystemUpdateState(SystemUpdateStates.Download, this.isManadatory);
    this.eventService.broadcastSystemUpdateState(this.systemUpdateState);
    this.validatePackage();
  }

  goToInstall(): void {
    this.index = 1;
    this.state = SystemUpdateTaskState.INSTALL;
    this.systemUpdateState = new SystemUpdateState(SystemUpdateStates.Install, this.isManadatory);
    this.eventService.broadcastSystemUpdateState(this.systemUpdateState);
  }

  goToComplete(): void {
    this.index = 2;
    this.state = SystemUpdateTaskState.COMPLETE;
    this.systemUpdateState = new SystemUpdateState(SystemUpdateStates.Complete, this.isManadatory);
    this.eventService.broadcastSystemUpdateState(this.systemUpdateState);
    if(this.isComplete && !this.isError){
      this.showUpdateSuccessDialog();
    }
  }

  validatePackage(): void {
    this.packageRows = [];
    this.isValidatingPackage = true;

    if (Utils.isSystemUpdateRequired(this.solutionInfo)) {
      this.softwareService.validateSystemUpdate().pipe(
        tap(resp => {
          let pkgRow: PackageRow = new PackageRow(
            "missing", "update", $localize`Update`, this.solutionInfo.manifestStatus.latestPatch.description, undefined, $localize`Install`,
            this.solutionInfo.manifestStatus.latestPatch);

          if (resp.body && resp.body.status) {
            pkgRow.status = Utils.getDownloadStatus(resp.body);
            this.needsDownload = resp.body.status != 'COMPLETE';
            this.isValidatingPackageComplete = true;
            this.isValidatingPackage = false;
          } else {
            pkgRow.status = { className: 'otsc-icon-status-available-download', altText: $localize`Available` };
            this.needsDownload = true;
          }
          pkgRow.url = this.solutionInfo.manifestStatus.latestPatch.details.downloadURL;
          this.packageRows.push(new DataTableRow(pkgRow, [], false, true, false, ""));
          this.isValidatingPackage = false;
        })
      ).subscribe();
    } else {
      this.isValidatingPackage = false;
      this.isValidatingPackageComplete = true;
      this.isUpToDate = true;
      this.goToComplete();
    }
  }

  openOfflineUpload(): void {
    this.modal.open(DragDropComponent, {
      data: {
        type: UploadFileType.UPDATE,
        supportMultiFiles: false,
        expectedFile: this.solutionInfo.manifestStatus.latestPatch.details.fileName,
        isSystemUpdate: true
      },
      id: 'otsc-drag-drop-dialog',
      customclass: 'ot-modal-side-panel ot-modal-side-panel-md'
    });
  }

  download(): void {
    let user: UserInfo = this.authService.getLoggedInUser();
    this.isOnline = user.settings.isOnlineMode;
    this.isProxyRequired = user.settings.proxyPrompt;
    this.itemsToDownload = new DownloadItem();

    this.packageRows.forEach(row => {
      this.itemsToDownload.updates.push(row.data.packageData);
    });

    Utils.startDownload(this.isProxyRequired, 0, "System Center",
      this.itemsToDownload, "", this.modal, this.dataService, this.softwareService);
  }

  private setDownloadInProgress(): void {
    this.systemUpdateState = new SystemUpdateState(SystemUpdateStates.Downloading, this.isManadatory);
    this.eventService.broadcastSystemUpdateState(this.systemUpdateState);
    this.isDownloading = true;

    let rows: DataTableRow[] = this.packageRows.filter((row: DataTableRow) => {
      return row.data.packageData.status != "COMPLETE";
    });

    if (rows.length > 0) {
      rows.forEach(row => {
        row.data.packageData.status = 'DOWNLOAD';
        row.data.status = Utils.getDownloadStatus(row.data.packageData);
      });
    }

    Utils.showDownloadStartedDialog(DownloadType.UPDATE, this.itemsToDownload.getTotalItems(), this.modal, this.cookieSrv);
  }

  processDownloadComplete(msg: MsgPayLoad): void {
    let rows: DataTableRow[] = this.packageRows.filter((row: DataTableRow) => {
      return row.data.type == "update" && row.data.packageData.id == msg.body.updateId;
    });

    if (rows.length > 0) {
      rows[0].data.packageData.status = msg.body.status;
      this.needsDownload = rows[0].data.packageData.status != 'COMPLETE';
      rows[0].data.status = Utils.getDownloadStatus(rows[0].data.packageData);
    }

    this.isValidatingPackage = false;
    this.isValidatingPackageComplete = true;
    this.isDownloading = false;
  }

  private install(): void {
    this.systemUpdateState = new SystemUpdateState(SystemUpdateStates.Installing, this.isManadatory);
    this.eventService.broadcastSystemUpdateState(this.systemUpdateState);
    this.isInstalling = true;
    this.installationProgress = $localize`Extracting update ...`;
    this.generalService.extractUpdate(this.solutionInfo.manifestStatus.latestPatch.details.fileName,
      this.solutionInfo.manifestStatus.latestPatch.details.hashcode).pipe(
        tap((resp: any) => {
          this.applyUpdate();
        }),
        catchError(resp => {
          this.errorMessage = $localize`An error has occurred while processing the update package.  The hash code does not match the manifest.`;
          this.isError = true;
          this.isInstalling = false;
          return of(resp);
        })).subscribe();
  }

  private applyUpdate(): void {
    let failedMsg: string = $localize`The system center update has failed to deploy.  Please check the update log for more information.`;
    this.installationProgress = $localize`Applying update ...`;
    this.generalService.applyUpdate().pipe(
      tap((resp: any) => {
        this.installTaskTimer = setTimeout(() => {
          this.errorMessage = failedMsg;
          this.isError = true;
          this.isInstalling = false;
          let msg = new MsgPayLoad(undefined, {});
          msg.setEvent(Events.CloseModals);
          msg.eventType = Events.CloseModals;
          this.eventService.broadcastEventMessage(msg);
        }, this.failedTime);
      }),
      catchError(resp => {
        this.errorMessage = failedMsg;
        this.isError = true;
        this.isInstalling = false;
        return of(resp);
      })).subscribe();
  }

  private showUpdateSuccessDialog(): void{
    const sub: Subscription =
      this.modal.open(ModalMessageComponent, {
        data: {
          title: $localize`System updated`,
          message: $localize`Your system has been successfully updated!  This page will now be refreshed.`,
          type: ModalMessageType.Success,
          primaryBtn: $localize`Close`,
          width: '500px',
        },
        id: 'otsc-system-update-success-dialog'
      }).afterClosed.subscribe((result: any) => {
        this.cancel();
        sub.unsubscribe();
      });
  }
}